/**
 * Calculates and prepares all data needed for the Patient Punctuality Donut Chart
 * @param {Array} data - The historical data from the API
 * @param {Object} options - Additional options like height, chartHeight
 * @returns {Object} - All data needed for rendering the Patient Punctuality in a BlankCard
 */
export const createPatientPunctuality = (data, options = {}) => {
  const {
    height = 350,
    chartHeight = 250,
    highThreshold = 80,
    lowThreshold = 60
  } = options

  // Process data to create punctuality statistics
  const processPunctualityData = () => {
    // Initialize counters for punctuality metrics
    let totalEarly = 0
    let totalOnTime = 0
    let totalLate = 0

    // Default thresholds if not provided
    const earlyThreshold = options.earlyThreshold || 30
    const lateThreshold = options.lateThreshold || 30

    let totalLateMinutes = 0
    let totalEarlyMinutes = 0
    let totalDays = 0
    
    // Process each day's data
    data.forEach(day => {
      if (!day.punctuality || !day.punctuality.counts) return
      
      // Add counts
      totalEarly += day.punctuality.counts.early || 0
      totalOnTime += day.punctuality.counts.onTime || 0
      totalLate += day.punctuality.counts.late || 0

      totalLateMinutes += day.punctuality.averageLate || 0
      totalEarlyMinutes += day.punctuality.averageEarly || 0
      totalDays += 1
    })
    
    // Calculate average minutes
    const avgEarlyMinutes = totalEarly > 0 ? (totalEarlyMinutes / totalDays) : 0
    const avgLateMinutes = totalLate > 0 ? (totalLateMinutes / totalDays) : 0
    
    return {
      early: totalEarly,
      onTime: totalOnTime,
      late: totalLate,
      earlyThreshold: earlyThreshold,
      lateThreshold: lateThreshold,
      stats: {
        avgEarlyTime: avgEarlyMinutes.toFixed(1) + " min",
        avgDelay: avgLateMinutes.toFixed(1) + " min"
      }
    }
  }
  
  const punctualityData = processPunctualityData()
  
  // Get status based on on-time percentage
  const getStatus = () => {

    const totalPatients = punctualityData.early + punctualityData.onTime + punctualityData.late
    const onTimePercent = totalPatients > 0 ? (punctualityData.onTime / totalPatients) * 100 : 0
      
    if (onTimePercent >= highThreshold) {
      return { 
        status: "great", 
        badge: "Exceeding Goal" 
      }
    }
    if (onTimePercent >= lowThreshold) {
      return { 
        status: "good", 
        badge: "Almost There" 
      }
    }
    return { 
      status: "critical", 
      badge: "Needs Attention"
    }
  }

  // Create labels for the donut chart
  const labels = [
    `Early (>${punctualityData.earlyThreshold} min)`,
    `On Time (±${punctualityData.lateThreshold} min)`,
    `Late (>${punctualityData.lateThreshold} min)`
  ]
  
  // Create legend for BlankCard
  const legend = [
    { label: labels[0], color: "#AEA2FF" },
    { label: labels[1], color: "#6955EA" },
    { label: labels[2], color: "#352D66" }
  ]
  
  const statusInfo = getStatus()

  // Add tooltip text
  const tooltipText = "Patient punctuality is measured as time difference between arrival and appointment time. Early arrivals are patients who arrive more than 30 minutes before their scheduled start time. Late arrivals are patients who arrive more than 30 minutes after their scheduled start time."

  return {
    title: "Patient Punctuality",
    description: "Distribution of patient arrivals relative to scheduled appointment start times",
    tooltip: tooltipText,
    chartType: "donut",
    badge: statusInfo.badge,
    status: statusInfo.status,
    // Pass data as an array of values - this is what DonutChart expects
    chartData: [
      punctualityData.early, 
      punctualityData.onTime, 
      punctualityData.late
    ],
    // Pass labels as an option
    options: {
      labels: labels
    },
    chartColors: ["#AEA2FF", "#6955EA", "#352D66"],
    chartHeight,
    height,
    stats: {
      avgEarlyArrival: punctualityData.stats.avgEarlyTime,
      avgLateArrival: punctualityData.stats.avgDelay
    },
    smallStats: true,
    legend: legend // Add legend for BlankCard to use
  }
}

export default createPatientPunctuality 